import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { BaseLayoutContext, BaseLayoutSection, DayOfWeek } from '../types/base-layout'
import { sanitizeHtmlContent } from '../utils/sanitize'
import { BasePhotosSection } from './sections/base-photos-section'
import { MissionStoriesSection } from './sections/mission-stories-section'
import { PrayerRequestsSection } from './sections/prayer-requests-section'
import { PartnerGiveListSection } from './sections/partner-give-list-section'

interface BaseSectionRendererProps {
  section: BaseLayoutSection
  context: BaseLayoutContext
}

const DAYS: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function titleCaseFromField(field?: string): string | undefined {
  if (!field) return undefined
  const match = field.match(/^(textBox)(\d+)$/i)
  if (match) {
    return `Text Box ${match[2]}`
  }
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function BaseSectionRenderer({ section, context }: BaseSectionRendererProps) {
  switch (section.type) {
    case 'textBox': {
      const field = typeof section.options?.['field'] === 'string' ? section.options?.['field'] : undefined
      const isRotating = section.options?.['isRotating'] === true
      
      let safeHtml: string | null = null
      
      if (isRotating) {
        // Get current day of week (0 = Sunday, 6 = Saturday)
        const today = new Date().getDay()
        const dayOfWeek: DayOfWeek = DAYS[today] as DayOfWeek
        
        // Get content for current day
        const optionDays = (section.options?.['days'] as Record<DayOfWeek, string | undefined>) ?? {}
        const dayContent = optionDays[dayOfWeek] ?? null
        safeHtml = sanitizeHtmlContent(dayContent)
      } else {
        // Regular textBox: use single content
        const rawContent = section.content ?? null
        safeHtml = sanitizeHtmlContent(rawContent)
      }

      return (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle>{section.title ?? titleCaseFromField(field) ?? 'Text Box'}</CardTitle>
          </CardHeader>
          <CardContent>
            {safeHtml ? (
              <div className="prose max-w-none text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: safeHtml }} />
            ) : (
              <p className="text-sm text-muted-foreground">No content configured</p>
            )}
          </CardContent>
        </Card>
      )
    }

    case 'dailyMessage': {
      const optionDays = (section.options?.['days'] as Record<string, string | undefined>) ?? {}
      const isRotating = section.options?.['isRotating'] === true
      
      const mergedDays = DAYS.reduce<Record<DayOfWeek, string | null>>((acc, day) => {
        const configured = optionDays[day]
        const fallback = context.dailyMessages[day]
        acc[day] = sanitizeHtmlContent(configured ?? fallback ?? null)
        return acc
      }, {
        sunday: null,
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
      })

      if (isRotating) {
        // Show only current day's content
        const today = new Date().getDay()
        const dayOfWeek: DayOfWeek = DAYS[today] as DayOfWeek
        const currentDayHtml = mergedDays[dayOfWeek]

        return (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle>{section.title ?? 'Daily Messages'}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {currentDayHtml ? (
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: currentDayHtml }} />
              ) : (
                <p>No message configured for today</p>
              )}
            </CardContent>
          </Card>
        )
      }

      // Show all days (original behavior)
      const hasAnyContent = DAYS.some((day) => mergedDays[day])

      return (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle>{section.title ?? 'Daily Messages'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            {hasAnyContent ? (
              DAYS.map((day) => {
                const html = mergedDays[day]
                return html ? (
                  <div key={day}>
                    <p className="font-medium capitalize text-foreground">{day}</p>
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                ) : null
              })
            ) : (
              <p>No daily messages configured</p>
            )}
          </CardContent>
        </Card>
      )
    }

    case 'basePhotos':
      return <BasePhotosSection key={section.id} section={section} context={context} />

    case 'missionStories':
      return <MissionStoriesSection key={section.id} section={section} context={context} />

    case 'prayerRequests':
      return <PrayerRequestsSection key={section.id} section={section} context={context} />

    case 'partnerGiveList':
      return <PartnerGiveListSection key={section.id} section={section} context={context} />

    default:
      return (
        <Card key={section.id}>
          <CardHeader>
            <CardTitle>{section.title ?? 'Section'}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              This section type ({section.type}) is not yet implemented.
            </p>
          </CardContent>
        </Card>
      )
  }
}

